/**
 * Hook owning the lightweight side effects shared by every typed encounter
 * form: engagement-status hydration on first mount, and "now"-default period
 * times for non-intake encounters.
 *
 * Pulled out of the legacy `Encounter.form.tsx` so each typed form gets a
 * narrowed effect surface (no scribe, no goal-progress, no duplicate-intake
 * guardrail). Only fires when the form has not yet been seeded.
 *
 * @agentic connections
 */

import { useEffect, useRef } from "react";
import type { Patient } from "@medplum/fhirtypes";
import type { UseFormReturnType } from "@mantine/form";
import { PATIENT_TAG_MAPPINGS } from "@benchmark/core/constants";
import type {
  CaseManagementInteractionFormData,
  ClinicalFollowUpInteractionFormData,
  CoCMFollowUpInteractionFormData,
  DischargeInteractionFormData,
  GeneralInteractionFormData,
  UnbilledFollowUpInteractionFormData,
} from "@benchmark/core/types";

/**
 * Typed-form shapes that share the non-intake "now defaults" behaviour. The
 * intake forms manage their own period via the scribe pipeline.
 *
 * @agentic connections
 */
export type NonIntakeEncounterFormData =
  | GeneralInteractionFormData
  | CaseManagementInteractionFormData
  | ClinicalFollowUpInteractionFormData
  | CoCMFollowUpInteractionFormData
  | UnbilledFollowUpInteractionFormData
  | DischargeInteractionFormData;

/**
 * Hydrates engagement-status from the patient meta tag (once) and seeds the
 * encounter period to "now" for non-intake forms when no start time is set.
 *
 * Intentionally narrow: the typed form decides what's "now" for itself. The
 * effect closes only over the patient ID and the current period start so it
 * cannot fire repeatedly when other unrelated fields change.
 *
 * @agentic connections
 */
export const useEncounterCommonFields = <T extends NonIntakeEncounterFormData>(
  form: UseFormReturnType<T>,
  patient: Patient | undefined,
  options: { isEditing: boolean } = { isEditing: false },
): void => {
  const seededRef = useRef(false);
  const { isEditing } = options;

  useEffect(() => {
    if (seededRef.current) return;
    if (!patient) return;
    seededRef.current = true;

    if (!isEditing) {
      const engagementTag = patient.meta?.tag?.find(
        (t) => t.system === PATIENT_TAG_MAPPINGS.engagementStatus.system,
      );
      if (engagementTag?.code) {
        form.setFieldValue(
          "patient-engagement-status",
          engagementTag.code as never,
        );
      }
    }

    const periodStart = (form.values as { period?: { start?: string } }).period
      ?.start;
    if (!periodStart) {
      const now = new Date().toISOString();
      form.setFieldValue("period.start", now as never);
      form.setFieldValue("period.end", now as never);
    }
  }, [patient, isEditing, form]);
};
